import type { ApiResponse } from '../_types/api'

const PRODUCTION_OPEN_ENDPOINT = 'https://api.quickcasa.ai/v1/open'

/**
 * Resolves which Open endpoint URL this build should target. Reads from the
 * `VITE_OPEN_ENDPOINT` env variable and falls back to the production URL.
 *
 * @returns {string} The base URL for the Open endpoint.
 */
const resolveOpenEndpoint = (): string => {
  const fromEnvironment = import.meta.env.VITE_OPEN_ENDPOINT
  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment
  }
  return PRODUCTION_OPEN_ENDPOINT
}

const OPEN_ENDPOINT = resolveOpenEndpoint()

if (import.meta.env.DEV) {
  console.info('[quote] doOpenCall target:', OPEN_ENDPOINT)
}

/**
 * Calls a public QuickCasa backend command via the `/v1/open` proxy. The
 * command name and its fields are wrapped in a top-level `data` object, which
 * is the convention the upstream `onRequest` function unwraps. Non-200
 * `ApiResponse.code` values are returned, not thrown, so callers can render the
 * right UI state.
 *
 * @param {string} command - Backend command name, e.g. `publicquote-get`.
 * @param {Record<string, unknown>} payload - Additional fields forwarded to the command.
 * @returns {Promise<ApiResponse>} The parsed API response.
 */
const doOpenCall = async (
  command: string,
  payload: Record<string, unknown> = {},
): Promise<ApiResponse> => {
  try {
    const response = await fetch(OPEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: {
          command,
          ...payload,
        },
      }),
    })

    if (!response.ok) {
      return {
        code: response.status,
        message: `Open endpoint returned HTTP ${response.status}`,
        data: '',
      }
    }

    const parsed = (await response.json()) as ApiResponse
    return parsed
  } catch (error) {
    return {
      code: 500,
      message: error instanceof Error ? error.message : 'Open call failed',
      data: '',
    }
  }
}

export { doOpenCall }
